import { useState, type FormEvent } from 'react';
import {
  telHref, formatPhone, buildWhatsAppUrl, whatsappMessage, mapEmbedUrl, directionsUrl,
  type GenericPageData, type MediaRef,
} from '@cng/shared';
import { useSite } from '@/app/page-data';
import { api, ApiError } from '@/services/api';
import { WhatsAppIcon } from '@/components/common/Icons';
import { AccentedLines } from '@/components/common/AccentedTitle';
import { SmartImage } from '@/components/common/SmartImage';
import { Link } from 'react-router-dom';

type Status =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'sent'; message: string }
  | { kind: 'error'; message: string; fields: Record<string, string> };

/** Accessible enquiry form with CMS-driven contact details and campaign imagery. */
export function ContactPage({ data }: { data: GenericPageData & { kind: 'contact' } }) {
  const { settings, branches } = useSite();
  const [status, setStatus] = useState<Status>({ kind: 'idle' });

  const page = data.page;
  const hero = page.sections.find((s) => s.type === 'hero')?.data ?? {};
  const info = page.sections.find((s) => s.key === 'contact-info')?.data ?? {};

  const photo = (hero.image ?? null) as MediaRef | null;
  const headline = String(hero.title ?? page.title);
  const headlineAccent = String(hero.titleAccent ?? '');
  const tag = String(hero.tag ?? 'Contact Us');
  const subtitle = String(hero.subtitle ?? '');

  // Contact details follow the active primary branch.
  const primary = branches[0] as (typeof branches)[number] & { hoursSummary?: string[] } | undefined;

  // Keep the map in sync with the CMS branch address.
  const mapSrc = primary ? mapEmbedUrl(primary) : '';

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const fd = new FormData(form);
    setStatus({ kind: 'sending' });

    try {
      const result = await api.post<{ id: string; message: string }>('/api/public/contact', {
        name: String(fd.get('name') ?? ''),
        phone: String(fd.get('phone') ?? ''),
        email: String(fd.get('email') ?? ''),
        branchId: String(fd.get('branchId') ?? '') || null,
        message: String(fd.get('message') ?? ''),
        sourcePage: page.path,
        company: String(fd.get('company') ?? ''),
      });
      setStatus({ kind: 'sent', message: result.message });
      form.reset();
    } catch (err) {
      const fields: Record<string, string> = {};
      if (err instanceof ApiError && err.details) {
        for (const d of err.details) fields[d.path] = d.message;
      }
      setStatus({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Something went wrong. Please try again.',
        fields,
      });
    }
  }

  const fieldError = (name: string) => (status.kind === 'error' ? status.fields[name] : undefined);

  return (
    <>
      <div className="cng-page-contact">
      <header className="contact-intro">
          <nav className="contact-breadcrumb" aria-label="Breadcrumb"><Link to="/">Home</Link><span aria-hidden="true">/</span><span>Contact</span></nav>
          <div className="section-tag">{tag}</div>
          <AccentedLines
            as="h1"
            className="contact-headline"
            text={headline}
            accent={headlineAccent}
            accentTag="span"
          />
          {subtitle ? <p className="contact-sub">{subtitle}</p> : null}
          <div className="contact-quick-links">
            {settings.primaryPhone ? <a href={telHref(settings.primaryPhone)}>Call {settings.primaryPhoneDisplay || formatPhone(settings.primaryPhone)}</a> : null}
            {settings.whatsappNumber ? <a href={buildWhatsAppUrl({ phone: settings.whatsappNumber, message: whatsappMessage('generalEnquiry', {}, settings.whatsappTemplates) })} target="_blank" rel="noopener noreferrer"><WhatsAppIcon size={18} /> WhatsApp us</a> : null}
          </div>
        </header>
        <div className="contact-wrapper">
        <section className="contact-left" aria-labelledby="contact-form-heading">
          <h2 id="contact-form-heading">Let&apos;s plan your next visit</h2>
          <p className="contact-form-intro">Tell us what you have in mind. For a same-day appointment, please call or WhatsApp the salon.</p>

          {status.kind === 'sent' ? (
            <div className="cg-form-success" role="status">
              <strong>Thank you!</strong>
              <p>{status.message}</p>
              <button type="button" onClick={() => setStatus({ kind: 'idle' })}>
                Send another message
              </button>
            </div>
          ) : (
            <form className="cg-form" method="post" action="/api/public/contact" onSubmit={onSubmit} noValidate>
              <input type="hidden" name="sourcePage" value={page.path} />
              {status.kind === 'error' ? (
                <p className="cg-form-error" role="alert">{status.message}</p>
              ) : null}

              <div className="form-row">
                <div className="input-group">
                  <label htmlFor="cf-name">Name <span>(required)</span></label>
                  <input
                    id="cf-name" name="name" type="text" className="cg-input"
                    placeholder="Your Name" required autoComplete="name"
                    aria-invalid={fieldError('name') ? true : undefined}
                    aria-describedby={fieldError('name') ? 'cf-name-err' : undefined}
                  />
                  {fieldError('name') ? (
                    <span className="cg-field-err" id="cf-name-err">{fieldError('name')}</span>
                  ) : null}
                </div>

                <div className="input-group">
                  <label htmlFor="cf-phone">Phone <span>(required)</span></label>
                  <input
                    id="cf-phone" name="phone" type="tel" className="cg-input"
                    placeholder="+91 00000 00000" required autoComplete="tel" inputMode="tel"
                    aria-invalid={fieldError('phone') ? true : undefined}
                    aria-describedby={fieldError('phone') ? 'cf-phone-err' : undefined}
                  />
                  {fieldError('phone') ? (
                    <span className="cg-field-err" id="cf-phone-err">{fieldError('phone')}</span>
                  ) : null}
                </div>
              </div>

              <div className="input-group">
                <label htmlFor="cf-email">Email <span>(optional)</span></label>
                <input
                  id="cf-email" name="email" type="email" className="cg-input"
                  placeholder="you@example.com" autoComplete="email"
                  aria-invalid={fieldError('email') ? true : undefined}
                  aria-describedby={fieldError('email') ? 'cf-email-err' : undefined}
                />
                {fieldError('email') ? (
                  <span className="cg-field-err" id="cf-email-err">{fieldError('email')}</span>
                ) : null}
              </div>

              {branches.length > 1 ? (
                <div className="input-group">
                  <label htmlFor="cf-branch">Which salon?</label>
                  <select id="cf-branch" name="branchId" className="cg-select" defaultValue="">
                    <option value="">No preference</option>
                    {branches.map((b) => (
                      <option value={b._id} key={b._id}>{b.shortName || b.name}</option>
                    ))}
                  </select>
                </div>
              ) : null}

              <div className="input-group">
                <label htmlFor="cf-message">Message <span>(required)</span></label>
                <textarea
                  id="cf-message" name="message" className="cg-textarea"
                  placeholder="How can we help you?" required
                  aria-invalid={fieldError('message') ? true : undefined}
                  aria-describedby={fieldError('message') ? 'cf-message-err' : undefined}
                />
                {fieldError('message') ? (
                  <span className="cg-field-err" id="cf-message-err">{fieldError('message')}</span>
                ) : null}
              </div>

              {/* Hidden from people, irresistible to bots. */}
              <div className="cg-hp" aria-hidden="true">
                <label htmlFor="cf-company">Company</label>
                <input id="cf-company" name="company" type="text" tabIndex={-1} autoComplete="off" />
              </div>

              <div className="btn-group">
                <button type="submit" className="cg-btn" disabled={status.kind === 'sending'}>
                  {status.kind === 'sending' ? 'Sending…' : 'Send enquiry'}
                </button>

              </div>
              <p className="contact-form-note">Sending an enquiry does not confirm an appointment. Our team will help arrange your visit.</p>
            </form>
          )}
        </section>

        <aside className="contact-right" aria-label="Salon location and contact details">
          {photo?.url ? <div className="contact-photo"><SmartImage media={photo} sizes="(max-width: 800px) calc(100vw - 40px), 560px" loading="eager" /></div> : null}
          <div className="info-card">
            <div className="info-item">
              <p className="contact-location-tag">ISRO Layout &middot; Bengaluru</p>
              <h2>{String(info.visitLabel ?? 'Visit Us')}</h2>
              <p>
                {(primary?.addressLines ?? []).map((line, i, arr) => (
                  <span key={line}>{line}{i < arr.length - 1 ? <br /> : null}</span>
                ))}
              </p>
              {primary ? <a className="contact-directions" href={directionsUrl(primary)} target="_blank" rel="noopener noreferrer">Get directions <span aria-hidden="true">&#8599;</span></a> : null}
            </div>

            <div className="info-item">
              <h2>{String(info.contactLabel ?? 'Contact')}</h2>
              {settings.primaryPhone ? (
                <p>
                  <a href={telHref(settings.primaryPhone)}>
                    {settings.primaryPhoneDisplay || formatPhone(settings.primaryPhone)}
                  </a>
                </p>
              ) : null}
              {settings.email ? (
                <p><a href={`mailto:${settings.email}`}>{settings.email}</a></p>
              ) : null}
            </div>

            <div className="info-item">
              <h2>{String(info.hoursLabel ?? 'Hours')}</h2>
              {(primary?.hoursSummary ?? []).map((line) => <p key={line}>{line}</p>)}
            </div>
          </div>
        </aside>
      </div>
      </div>

      {mapSrc ? (
        <section className="cng-contact-map" aria-labelledby="contact-map-heading">
          <div className="contact-map-intro"><h2 id="contact-map-heading">Find your way to Charlie &amp; Guys</h2><p>Visit our ISRO Layout salon on Vasanthapura Main Road, below Fitness Factory Gym.</p><Link to="/locations/isro-layout/">Explore our salon &rarr;</Link></div>
          <iframe
            src={mapSrc}
            title={`Map of ${primary?.shortName || primary?.name || 'the salon'}`}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </section>
      ) : null}

    </>
  );
}
